import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Mail, Clock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { cn } from '@/lib/utils';

import { useFundEmails } from '@/hooks/react-query/useFunds';
import { formatDateTime } from '@/utils/formatters';

// Dialog detail panel
import { EmailDetailPanel } from './emails/EmailDetailPanel';
import { EmailStatusBadge } from './emails/EmailStatusBadge';
import { renderSubject } from './emails/EmailUtils';

interface FundEmailsTabProps {
  fundId: string;
}

export function FundEmailsTab({ fundId }: FundEmailsTabProps) {
  const navigate = useNavigate();
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);

  const { data: emails, isLoading, error } = useFundEmails(fundId);

  const handleSelectEmail = (batchId: string) => {
    setSelectedBatchId(batchId === selectedBatchId ? null : batchId);
  };

  const handleCloseDetail = () => {
    setSelectedBatchId(null);
  };

  const handleComposeClick = () => {
    navigate(`/app/funds/${fundId}/email-compose`);
  };

  if (isLoading) {
    return (
      <TabsContent
        value="emails"
        className="h-[calc(100vh-220px)] min-h-[500px]"
      >
        <div className="space-y-4">
          <div className="animate-pulse rounded-md bg-muted h-10 w-full" />
          <div className="animate-pulse rounded-md bg-muted h-24 w-full" />
          <div className="animate-pulse rounded-md bg-muted h-24 w-full" />
        </div>
      </TabsContent>
    );
  }

  if (error) {
    return (
      <TabsContent
        value="emails"
        className="h-[calc(100vh-220px)] min-h-[500px]"
      >
        <div className="p-8 text-center text-muted-foreground">
          Failed to load email history.
        </div>
      </TabsContent>
    );
  }

  return (
    <TabsContent value="emails" className="h-[calc(100vh-220px)] min-h-[500px]">
      <div className="flex flex-col h-full">
        {/* Main List */}
        <div className="flex-1 flex flex-col min-w-0 bg-background rounded-lg border shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-muted/40 font-medium text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>Sent Emails</span>
              <span className="text-xs text-muted-foreground">
                {emails?.length || 0} total
              </span>
            </div>
            <Button size="sm" variant="outline" onClick={handleComposeClick}>
              <Mail className="w-3.5 h-3.5 mr-2" />
              Email Investors
            </Button>
          </div>

          {!emails || emails.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center text-muted-foreground h-full">
              <div className="bg-primary/10 p-3 rounded-full mb-4">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No emails sent yet</h3>
              <p className="max-w-sm mb-6 text-sm">
                Emails sent to investors for this fund will appear here.
              </p>
              <Button onClick={handleComposeClick}>Compose Email</Button>
            </div>
          ) : (
            <ScrollArea className="flex-1">
              <Table>
                <TableHeader className="bg-muted/40 sticky top-0 z-10 border-b">
                  <TableRow className="hover:bg-muted/40 h-9 border-b-0">
                    <TableHead className="font-medium text-xs h-9 pl-6">
                      Subject
                    </TableHead>
                    <TableHead className="font-medium text-xs h-9 w-[150px]">
                      Status
                    </TableHead>
                    <TableHead className="font-medium text-xs h-9 w-[180px]">
                      Date
                    </TableHead>
                    <TableHead className="font-medium text-xs h-9 text-right pr-6 w-[120px]">
                      Recipients
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emails.map((email) => {
                    const isSelected = selectedBatchId === email.batch_id;
                    return (
                      <TableRow
                        key={email.batch_id}
                        onClick={() => handleSelectEmail(email.batch_id)}
                        className={cn(
                          'cursor-pointer transition-colors hover:bg-muted/30 h-14 border-b border-border/50',
                          isSelected && 'bg-muted/50'
                        )}
                      >
                        <TableCell className="pl-6 py-3 font-medium text-sm max-w-[400px] truncate">
                          {renderSubject(email.subject)}
                        </TableCell>
                        <TableCell className="py-3">
                          <EmailStatusBadge status={email.status} />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground py-3">
                          <div className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDateTime(email.created_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right pr-6 py-3 text-xs text-muted-foreground">
                          {email.recipient_count} recipients
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </div>

        {/* Dialog Detail Panel */}
        <Dialog
          open={!!selectedBatchId}
          onOpenChange={(open) => !open && handleCloseDetail()}
        >
          <DialogContent className="sm:max-w-[750px] h-[85vh] p-0 flex flex-col overflow-hidden gap-0 border-0 rounded-lg">
            {selectedBatchId && (
              <EmailDetailPanel fundId={fundId} batchId={selectedBatchId} />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TabsContent>
  );
}
